import { useCallback, useEffect, useRef, useState } from "react";

/* Hooks de movimento da landing PROSEG PAY (port da lógica do design). */

const REDUCE_QUERY = "(prefers-reduced-motion: reduce)";

function matchReduced() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(REDUCE_QUERY).matches;
}

function subscribe(mq, handler) {
  if (mq.addEventListener) mq.addEventListener("change", handler);
  else mq.addListener(handler);
  return () => {
    if (mq.removeEventListener) mq.removeEventListener("change", handler);
    else mq.removeListener(handler);
  };
}

/** Nível de animação: 1 = cinematográfica (padrão), 0 = desligada (reduced motion). */
export function useMotionLevel() {
  const [level, setLevel] = useState(() => (matchReduced() ? 0 : 1));

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(REDUCE_QUERY);
    return subscribe(mq, () => setLevel(mq.matches ? 0 : 1));
  }, []);

  return level;
}

/** Media query booleana (ex.: "(max-width: 959px)"). */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    setMatches(mq.matches);
    return subscribe(mq, () => setMatches(mq.matches));
  }, [query]);

  return matches;
}

/** true quando a página rolou além de `threshold` px. */
export function useScrolled(threshold = 24) {
  const [scrolled, setScrolled] = useState(
    () => typeof window !== "undefined" && window.scrollY > threshold
  );

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      setScrolled(window.scrollY > threshold);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [threshold]);

  return scrolled;
}

/**
 * Reveal on scroll: elementos `.rv` / `.rv-child` dentro de `rootRef` ganham
 * `data-rv="on"` ao entrar na viewport; `data-delay` vira transition-delay.
 * O marcador é um atributo (e não uma classe) para sobreviver a re-renders do
 * React que reescrevem `className` (ex.: item do FAQ ao abrir/fechar).
 */
export function useReveal(rootRef) {
  useEffect(() => {
    const root = rootRef.current || document.body;

    const show = (el) => {
      const target = el.classList.contains("rv-child")
        ? el.firstElementChild || el
        : el;
      target.style.transitionDelay = `${el.dataset.delay || 0}ms`;
      el.setAttribute("data-rv", "on");
    };

    const pending = () =>
      root.querySelectorAll(".rv:not([data-rv]), .rv-child:not([data-rv])");

    if (!("IntersectionObserver" in window) || matchReduced()) {
      pending().forEach((el) => el.setAttribute("data-rv", "on"));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          show(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -6% 0px" }
    );

    const seen = new WeakSet();
    const scan = () =>
      pending().forEach((el) => {
        if (seen.has(el)) return;
        seen.add(el);
        io.observe(el);
      });

    scan();
    const mo = new MutationObserver(scan);
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [rootRef]);
}

/** Parallax do hero: texto sobe/esmaece e o celular sobe mais devagar. */
export function useHeroParallax(copyRef, visualRef, level) {
  useEffect(() => {
    const copy = copyRef.current;
    const visual = visualRef.current;
    if (!level || !copy || !visual) return undefined;

    let raf = 0;
    const update = () => {
      raf = 0;
      const y = window.scrollY;
      copy.style.transform = `translateY(${(y * 0.14).toFixed(1)}px)`;
      copy.style.opacity = String(Math.max(0, 1 - y / 640));
      visual.style.transform = `translateY(${(y * 0.07).toFixed(1)}px)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
      copy.style.transform = "";
      copy.style.opacity = "";
      visual.style.transform = "";
    };
  }, [copyRef, visualRef, level]);
}

export const HERO_RIBBONS = {
  rot: -0.4,
  speed: 1,
  ribbons: [
    { y: 0.5, amp: 0.12, th: 0.19, speed: 1.0, freq: 1.1, phase: 0.0, alpha: 0.95 },
    { y: 0.42, amp: 0.09, th: 0.07, speed: 0.8, freq: 1.7, phase: 2.4, alpha: 0.42 },
    { y: 0.6, amp: 0.07, th: 0.05, speed: 1.2, freq: 0.8, phase: 4.1, alpha: 0.32 },
  ],
};

export const CTA_RIBBONS = {
  rot: -0.22,
  speed: 0.8,
  ribbons: [
    { y: 0.5, amp: 0.12, th: 0.14, speed: 1.0, freq: 1.0, phase: 1.0, alpha: 0.62 },
    { y: 0.62, amp: 0.08, th: 0.05, speed: 0.9, freq: 1.5, phase: 3.0, alpha: 0.3 },
  ],
};

/**
 * Fitas de luz animadas em <canvas> (hero e CTA final).
 * O loop de requestAnimationFrame só roda enquanto o canvas está visível e há
 * movimento; com reduced motion desenha um quadro parado e para.
 */
export function useRibbon(canvasRef, opts, level) {
  const levelRef = useRef(level);
  const kickRef = useRef(null);

  useEffect(() => {
    levelRef.current = level;
    if (kickRef.current) kickRef.current(true);
  }, [level]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    const parent = canvas.parentElement;

    const st = { t: Math.random() * 40, raf: 0, vis: true, w: 1, h: 1, dpr: 1, still: false };

    const render = () => {
      const { w, h, dpr } = st;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.save();
      ctx.translate(w / 2, h / 2);
      ctx.rotate(opts.rot);
      ctx.translate(-w / 2, -h / 2);

      const L = -w * 0.6;
      const R = w * 1.6;
      const step = Math.max(6, w / 160);

      for (const rb of opts.ribbons) {
        const cy = h * rb.y;
        const amp = h * rb.amp;
        const T = st.t * rb.speed + rb.phase;
        const top = (x) => {
          const u = x / w;
          return (
            cy +
            Math.sin(u * rb.freq * 6.283 + T) * amp +
            Math.sin(u * rb.freq * 3.1 - T * 0.7 + 1.3) * amp * 0.45
          );
        };
        const th = (x) =>
          h * rb.th * (0.12 + 0.88 * (0.5 + 0.5 * Math.sin((x / w) * 4.2 + T * 1.1 + rb.phase)));

        ctx.beginPath();
        for (let x = L; x <= R; x += step) {
          const y = top(x);
          if (x === L) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        for (let x = R; x >= L; x -= step) ctx.lineTo(x, top(x) + th(x));
        ctx.closePath();

        const g = ctx.createLinearGradient(L, 0, R, 0);
        g.addColorStop(0, "rgba(7,29,59,0)");
        g.addColorStop(0.2, "rgba(10,63,143,1)");
        g.addColorStop(0.45, "#0878F9");
        g.addColorStop(0.62, "#19C6E8");
        g.addColorStop(0.8, "#0878F9");
        g.addColorStop(1, "rgba(7,29,59,0)");
        ctx.globalAlpha = rb.alpha;
        ctx.fillStyle = g;
        ctx.fill();

        const hg = ctx.createLinearGradient(L, 0, R, 0);
        hg.addColorStop(0, "rgba(255,255,255,0)");
        hg.addColorStop(0.5, "rgba(255,255,255,.55)");
        hg.addColorStop(1, "rgba(255,255,255,0)");
        ctx.globalAlpha = rb.alpha * 0.6;
        ctx.lineWidth = 1.4;
        ctx.strokeStyle = hg;
        ctx.beginPath();
        for (let x = L; x <= R; x += step) {
          const y = top(x) + th(x) * 0.16;
          if (x === L) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      ctx.restore();
    };

    const draw = () => {
      st.raf = 0;
      if (!st.vis) return;
      const m = levelRef.current;
      if (m === 0) {
        if (st.still) return;
        st.still = true;
      } else {
        st.still = false;
      }
      st.t += 0.0042 * (m || 1) * opts.speed;
      render();
      if (m !== 0) st.raf = requestAnimationFrame(draw);
    };

    const kick = (reset) => {
      if (reset) st.still = false;
      if (!st.raf) st.raf = requestAnimationFrame(draw);
    };
    kickRef.current = kick;

    const resize = () => {
      const r = parent.getBoundingClientRect();
      st.dpr = Math.min(window.devicePixelRatio || 1, 2);
      st.w = Math.max(1, r.width);
      st.h = Math.max(1, r.height);
      canvas.width = st.w * st.dpr;
      canvas.height = st.h * st.dpr;
      kick(true);
    };
    resize();

    let ro = null;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(resize);
      ro.observe(parent);
    } else {
      window.addEventListener("resize", resize);
    }

    let io = null;
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver((entries) => {
        st.vis = entries[0].isIntersecting;
        if (st.vis) kick(false);
      });
      io.observe(canvas);
    }

    return () => {
      kickRef.current = null;
      if (st.raf) cancelAnimationFrame(st.raf);
      st.raf = 0;
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", resize);
      if (io) io.disconnect();
    };
  }, [canvasRef, opts]);
}

function restPose(base) {
  if (!base) return "rotateY(0deg) rotateX(0deg)";
  return `${base.pre || ""}rotateY(${base.ry}deg) rotateX(${base.rx}deg)`;
}

/** Inclinação 3D que segue o mouse (celular do hero, cartão). */
export function useTilt(ref, { max = 10, base = null, enabled = true } = {}) {
  const onMouseMove = useCallback(
    (e) => {
      const el = ref.current;
      if (!el || !enabled) return;
      const r = e.currentTarget.getBoundingClientRect();
      const dx = (e.clientX - r.left) / r.width - 0.5;
      const dy = (e.clientY - r.top) / r.height - 0.5;
      const b = base || { pre: "", ry: 0, rx: 0 };
      const ry = (b.ry + dx * max).toFixed(2);
      const rx = (b.rx - dy * max).toFixed(2);
      el.style.transform = `${b.pre || ""}rotateY(${ry}deg) rotateX(${rx}deg)`;
    },
    [ref, max, base, enabled]
  );

  const onMouseLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = restPose(base);
  }, [ref, base]);

  return { onMouseMove, onMouseLeave };
}

/** Contador animado do saldo (formatação pt-BR, como no design). */
export function useCountUp(ref, target, level, { duration = 1900, delay = 500 } = {}) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !level) return undefined;
    const node = el.firstChild;
    if (!node || node.nodeType !== 3) return undefined;
    const original = node.nodeValue;
    const fmt = (v) =>
      `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    let raf = 0;
    let t0 = 0;
    const tick = (now) => {
      if (!t0) t0 = now;
      const p = Math.min(1, (now - t0) / duration);
      const e = 1 - Math.pow(1 - p, 4);
      node.nodeValue = fmt(target * e);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    const timer = setTimeout(() => {
      raf = requestAnimationFrame(tick);
    }, delay);

    return () => {
      clearTimeout(timer);
      if (raf) cancelAnimationFrame(raf);
      node.nodeValue = original;
    };
  }, [ref, target, level, duration, delay]);
}
